import type { Filter, FilterOperator, OrderBy } from "./query.js";

/**
 * REST resource contracts.
 *
 * The generic gateway took the table name in the request body, which made every
 * read a `POST /data/select` and left the surface untyped: one route, one Lambda
 * and one permission for twenty-one entities. Each entity broken out of it gets
 * a resource of its own —
 *
 *   GET    /companies            list
 *   GET    /companies/{id}       read one
 *   POST   /companies            create
 *   PATCH  /companies/{id}       partial update
 *   DELETE /companies/{id}       delete
 *
 * — so the method carries the operation and the path carries the entity. Each
 * entity's paths are served by a function of its own, mounted on one API Gateway
 * `{proxy+}` and matched by `services/api/src/lib/router.ts`; a slow company
 * list cannot crowd out invoicing, and adding a route costs nothing in the
 * CloudFormation stack.
 *
 * List options travel as query parameters in PostgREST's spelling, which is the
 * vocabulary the pages already speak: `?select=id,legal_name&order=legal_name.asc`
 * `&status=eq.active`. Serialising here and parsing in the Lambda from the same
 * module keeps the two ends from drifting.
 */

export const COUNTRIES_PATH = "/countries";
export const CLIENT_GROUPS_PATH = "/client-groups";
export const COMPANIES_PATH = "/companies";
/**
 * A company's AFR history, and the one operation that moves it.
 *
 * Nested under the company because a financial year has no meaning away from the
 * company it belongs to — and because `companies` is already an API mount, so
 * none of this costs a new one.
 *
 * `POST .../file` is a route rather than a `PATCH` of the filing date, and that
 * is the whole design. Filing does two things that must not come apart: it
 * closes this year, and it opens next year. Left as a column patch, somebody
 * types a filing date, the row goes to `filed`, and the company is left with no
 * open period at all — or worse, with its copied `afr_due_date` still showing
 * the year just filed. One endpoint, both effects, or neither.
 *
 * {@link AFR_FILINGS_PATH} is the cross-company collection the dashboard widget,
 * the register view and the history export read. It sits under the same mount
 * and wins over `/companies/{companyId}` on its own, because the router puts a
 * literal segment ahead of a parameter whatever order the table lists them in.
 */
export const AFR_FILINGS_PATH = "/companies/afr-filings";
/** The kinds of document an AFR can carry — reference data, maintained in place. */
export const AFR_DOCUMENT_KINDS_PATH = "/companies/afr-document-kinds";
/**
 * Every AFR document, across companies — the read behind the export.
 *
 * Per-filing reads would be one request per financial year on the sheet, which
 * for a filtered register of a few hundred years is a few hundred round trips
 * before the first row is written.
 */
export const AFR_DOCUMENTS_PATH = "/companies/afr-documents";
export const companyAfrFilingsPath = (companyId: string) =>
  `${COMPANIES_PATH}/${encodeURIComponent(companyId)}/afr-filings`;
export const companyAfrFilingPath = (companyId: string, filingId: string) =>
  `${companyAfrFilingsPath(companyId)}/${encodeURIComponent(filingId)}`;
export const companyAfrFilePath = (companyId: string, filingId: string) =>
  `${companyAfrFilingPath(companyId, filingId)}/file`;

/**
 * The files behind one financial year's return — the same three-step shape the
 * payroll returns and the employee documents use: ask for a grant, PUT the bytes
 * straight to storage, then register the row.
 *
 * Three steps rather than one multipart POST because the bytes never pass
 * through the Lambda: a 25MB set of audited accounts would be a 25MB request
 * body to buffer, base64-encoded by API Gateway on the way in.
 */
export const companyAfrDocumentsPath = (companyId: string, filingId: string) =>
  `${companyAfrFilingPath(companyId, filingId)}/documents`;
export const companyAfrDocumentUploadUrlPath = (companyId: string, filingId: string) =>
  `${companyAfrDocumentsPath(companyId, filingId)}/upload-url`;
export const companyAfrDocumentDownloadPath = (
  companyId: string,
  filingId: string,
  documentId: string,
) => `${companyAfrDocumentsPath(companyId, filingId)}/${encodeURIComponent(documentId)}/download`;

export const CONTACTS_PATH = "/contacts";
export const SERVICE_CATEGORIES_PATH = "/service-categories";
export const SERVICES_PATH = "/services";
/**
 * Named sets of services that are delivered together, and the items of each.
 *
 * The items are a nested collection rather than a top-level resource, like a
 * contact's roles: an item has no meaning away from its bundle, and every read
 * and every write is "the services in this bundle". `PUT .../items` replaces
 * that whole set in one request — the editor's job is "the bundle is these
 * services at these quantities", and doing it as a diff of deletes and inserts
 * from the browser leaves a bundle half-rewritten whenever one call fails.
 *
 * `GET .../items` returns each item with the service it names — the name, the
 * code, the invoice wording and the published fee — because every screen that
 * expands a bundle needs those to build a line, and the alternative is a request
 * per item or the whole catalogue held in memory.
 *
 * `DELETE /service-bundles/{id}` is a real delete, unlike `/services`, which
 * marks inactive. Nothing stores a bundle id: a bundle expands into ordinary lines and
 * is not referred to again, so there is nothing for a delete to strand.
 */
export const SERVICE_BUNDLES_PATH = "/service-bundles";
export const serviceBundleItemsPath = (bundleId: string) =>
  `${SERVICE_BUNDLES_PATH}/${encodeURIComponent(bundleId)}/items`;

export const BANK_ACCOUNTS_PATH = "/bank-accounts";
/**
 * The wire details every bank account shares — one row, so the collection is
 * read with `GET /remittance-defaults` and the row patched by its own id. The
 * server refuses a second create rather than leaving two rows to choose from.
 */
export const REMITTANCE_DEFAULTS_PATH = "/remittance-defaults";
/**
 * Invoicing's own settings — one row, read with `GET /invoice-settings` and
 * patched by its id, the same shape as the remittance defaults.
 */
export const INVOICE_SETTINGS_PATH = "/invoice-settings";
/**
 * The reusable wording an invoice can tick on — "subject to BVI jurisdiction",
 * "payment net of bank charges". It is a list of rows rather than free text on
 * each invoice so the same sentence is not retyped, slightly differently, on
 * every invoice that needs it.
 */
export const INVOICE_NOTES_PATH = "/invoice-notes";
/**
 * The whole list's order, set in one request.
 *
 * Dragging one note up moves every note it passed, so the write is "the list now
 * reads in this order" rather than a `sort_order` per row. As one PATCH per row
 * from the browser it was a request for each note that moved, and any one of them
 * failing left the list half-renumbered — two notes sharing a position, or a gap —
 * which the invoice's note picker then draws in whichever order storage returned.
 *
 * A literal segment beside `{invoiceNoteId}`, as `/fee-schedules/standard` is
 * beside `{feeScheduleId}`: the router matches the literal first, and no note's
 * id can collide with it since ids are UUIDs.
 */
export const invoiceNotesReorderPath = `${INVOICE_NOTES_PATH}/reorder`;
/**
 * The register of bulk actions, read-only: `GET` lists them and
 * `GET …/{bulkActionId}/files/{fileIndex}/download` hands out a short link to
 * one of the files an action left, for as long as the action is listed.
 */
export const BULK_ACTIONS_PATH = "/bulk-actions";
export const bulkActionFileDownloadPath = (bulkActionId: string, fileIndex: number) =>
  `${BULK_ACTIONS_PATH}/${encodeURIComponent(bulkActionId)}/files/${fileIndex}/download`;
export const INVOICES_PATH = "/invoices";
/**
 * Bulk runs, done on the server: `POST` starts one from companies and bundles,
 * `GET …/{runId}` reports its progress, and `GET …/{runId}/download` hands out a
 * short link to the combined PDF while the run's own link has not expired.
 */
export const INVOICE_RUNS_PATH = `${INVOICES_PATH}/runs`;
export const invoiceRunPath = (runId: string) =>
  `${INVOICE_RUNS_PATH}/${encodeURIComponent(runId)}`;
export const invoiceRunDownloadPath = (runId: string) => `${invoiceRunPath(runId)}/download`;
/** The page in the app a run's email links to; it signs in, then downloads. */
export const invoiceRunAppPath = (runId: string) => `/invoice-runs/${encodeURIComponent(runId)}`;

/**
 * Fee schedules, and the priced lines under each one.
 *
 * The lines are a nested collection rather than a top-level resource because a
 * line has no meaning away from its schedule: its natural key is
 * `(fee_schedule_id, service_id)`, and every read and every write is "the prices
 * on this schedule". `PUT .../lines` replaces that whole set in one request —
 * the editor's job is "these services at these prices", and doing it as a diff
 * of deletes, updates and inserts from the browser left a schedule half-repriced
 * whenever one of those calls failed.
 *
 * `POST .../activate` is separate from a `PATCH` of `active` because activating
 * is not a field change: one target carries one active schedule, so switching
 * this one on has to switch off whichever held it and move the target's pointer
 * across. Done from the browser that was three requests with two windows in
 * which a company had either two active schedules or none.
 */
export const FEE_SCHEDULES_PATH = "/fee-schedules";
/**
 * The catalogue's own list, with the rates in force on a date.
 *
 * A literal segment beside `{feeScheduleId}`, as `/invoices/next-number` is beside
 * `{invoiceId}`: the router matches the literal first, and no schedule's id can
 * collide with it since ids are UUIDs.
 */
export const feeScheduleStandardPath = `${FEE_SCHEDULES_PATH}/standard`;
/**
 * Every schedule, every dated version and every price, in one request.
 *
 * What an export of the service catalogue "with its pricing" actually needs. Built
 * from the public routes it is one request for the schedules, one per schedule for
 * its versions and one per version for its prices — so twenty schedules of ten
 * versions is two hundred round trips, run from a browser, with no point at which
 * the set is consistent. Server-side it is one partition query per schedule,
 * because a schedule's versions and its prices share a partition.
 *
 *   ?as_of=YYYY-MM-DD   which version counts as in force; defaults to today
 *   ?versions=in_force  only that version per schedule — the default, and what an
 *                       export of current rates wants
 *   ?versions=all       the whole dated history of every schedule
 *   ?fee_schedule_id=   just this schedule, as its own export reads it; 404 if
 *                       there is no such schedule
 *
 * A literal segment beside `{feeScheduleId}`, like `/standard`: the router matches
 * the literal first, and no schedule's id can collide with it since ids are UUIDs.
 */
export const feeSchedulePriceBookPath = `${FEE_SCHEDULES_PATH}/price-book`;
export const feeScheduleClientGroupsPath = (feeScheduleId: string) =>
  `${FEE_SCHEDULES_PATH}/${encodeURIComponent(feeScheduleId)}/client-groups`;
export const feeScheduleVersionsPath = (feeScheduleId: string) =>
  `${FEE_SCHEDULES_PATH}/${encodeURIComponent(feeScheduleId)}/versions`;
/**
 * One dated version, addressed by its date.
 *
 * The date is the identity — there is no separate version id, because a version
 * is just the set of prices sharing an `effective_from`. That makes the URL
 * readable and bookmarkable.
 */
export const feeScheduleVersionPath = (feeScheduleId: string, effectiveFrom: string) =>
  `${feeScheduleVersionsPath(feeScheduleId)}/${encodeURIComponent(effectiveFrom)}`;
/** The prices of one dated version. */
export const feeScheduleLinesPath = (feeScheduleId: string, effectiveFrom: string) =>
  `${feeScheduleVersionPath(feeScheduleId, effectiveFrom)}/prices`;
/**
 * One service, across the schedules that price it.
 *
 * `GET` is the cascade read backwards: what each schedule charges for this service
 * today. `POST` is the write that fills those rates in when the service is created,
 * applying each to every dated version of its schedule — a service the schedules
 * have never priced has no rate to preserve on the earlier versions, only a hole
 * that bills the catalogue fee.
 */
export const serviceSchedulesPath = (serviceId: string) =>
  `${SERVICES_PATH}/${encodeURIComponent(serviceId)}/fee-schedules`;
export const feeScheduleActivatePath = (feeScheduleId: string) =>
  `${FEE_SCHEDULES_PATH}/${encodeURIComponent(feeScheduleId)}/activate`;

/**
 * The work log is one page over two tables: billable events against a service,
 * and sundry expenses recharged to a client. They are separate entities with
 * separate write roles, but nobody uses one without the other, so they sit as
 * two collections under the page's own path rather than as two top-level
 * resources whose kinship the URL never mentions.
 */
export const WORK_LOG_PATH = "/work-log";
export const WORK_LOG_EVENTS_PATH = `${WORK_LOG_PATH}/events`;
export const WORK_LOG_EXPENSES_PATH = `${WORK_LOG_PATH}/expenses`;

/**
 * The caller themselves: who they are, and what is waiting on them.
 *
 * `GET /me/notifications` sits here rather than under a mount of its own because
 * the feed has no entity behind it — it is "this caller, across four tables",
 * which is the same sentence `/me` already answers with roles and an employee id.
 * A `/notifications` mount would also be a new Lambda and a new API Gateway pair
 * to serve one read.
 *
 * It takes no parameters. What is in the feed is decided entirely by who is
 * asking: an approver sees the queue for the people they decide for, everybody
 * sees what was decided about their own requests. A query parameter that could
 * widen it would be a hole in the scoping, and one that narrowed it would be a
 * filter on a list short enough to read.
 */
export const ME_PATH = "/me";
export const meNotificationsPath = `${ME_PATH}/notifications`;

/**
 * The VIRRGIN account: the float Somasi holds with the BVI registry, and every
 * fee drawn against it.
 *
 * Two routes beyond the usual five, because two of this register's questions are
 * not a list of it:
 *
 *  - `/import` reads a statement. Which company a line belongs to and whether it
 *    is already stored are both answers the register has to give — the first
 *    needs every company's registry numbers, the second an exact lookup against
 *    rows that already number in the thousands — so the statement is sent here
 *    to be read rather than resolved in a browser against two full downloads.
 *    `dry_run` asks for the reading without the write, which is the preview an
 *    operator approves before anything is stored.
 *
 *  - `/summary` is the float. What is in the account and how much of what has
 *    been drawn is still to be recovered are sums over every transaction ever
 *    imported, and a list endpoint answers with a page: past a thousand rows the
 *    figures on screen would quietly become the figures for the most recent
 *    thousand. It carries the per-company unrecovered rollup too, which is the
 *    same scan and what the dashboard asks for.
 *
 *  - `/rematch` puts the registry numbers that matched nothing back to the
 *    register. Most of a statement's unmatched charges are fees for companies
 *    incorporated since the company list was last imported, so they arrive
 *    unmatched and become matchable the moment those companies exist — but
 *    re-importing the statement will not do it, because every line of it is by
 *    then a duplicate. Without this the only way back is a picker per row, and a
 *    month of new incorporations is a few hundred of them.
 */
export const VIRRGIN_TRANSACTIONS_PATH = "/virrgin-transactions";
export const virrginImportPath = `${VIRRGIN_TRANSACTIONS_PATH}/import`;
export const virrginSummaryPath = `${VIRRGIN_TRANSACTIONS_PATH}/summary`;
export const virrginRematchPath = `${VIRRGIN_TRANSACTIONS_PATH}/rematch`;

/** Query parameters the list endpoints understand, beyond column filters. */
export const RESERVED_LIST_PARAMS = [
  "select",
  "order",
  "or",
  "limit",
  "offset",
  "count",
  "head",
] as const;

const FILTER_OPERATORS: readonly FilterOperator[] = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "like",
  "ilike",
  "in",
  "is",
  "contains",
];

export interface ListQuery {
  /** Column projection; omitted or `["*"]` returns whole rows. */
  select?: string[];
  /** All of these must match. */
  filters?: Filter[];
  /**
   * OR group: a row matches when any one of these does, and the group as a whole
   * is ANDed with `filters`. This is what a search box needs — "the text appears
   * in the code or the name or the description" — which `filters` alone, being a
   * conjunction, cannot say.
   */
  or?: Filter[];
  order?: OrderBy[];
  limit?: number;
  offset?: number;
  /** Ask for an exact total alongside the page. */
  count?: boolean;
  /** Return the total only, with no rows. */
  head?: boolean;
}

function encodeValue(op: FilterOperator, value: unknown): string {
  if (op === "in") {
    const values = Array.isArray(value) ? value : [value];
    return `(${values.map((v) => String(v ?? "")).join(",")})`;
  }
  if (value === null || value === undefined) return "null";
  return String(value);
}

/**
 * `status.eq.active`, or `status.not.eq.active` — one member of an OR group.
 *
 * The value is percent-encoded inside the member, so a comma, a bracket or a
 * dot someone typed into a search box cannot be read as the grammar that
 * separates the members from each other. The parameter is then encoded again on
 * its way into the query string, and `parseMember` undoes this inner layer
 * after the outer one has been stripped.
 */
function encodeMember(filter: Filter): string {
  const prefix = filter.negate ? "not." : "";
  return `${filter.column}.${prefix}${filter.op}.${encodeURIComponent(
    encodeValue(filter.op, filter.value),
  )}`;
}

/** Serialises list options into a query string (without the leading `?`). */
export function encodeListQuery(query: ListQuery = {}): string {
  const params = new URLSearchParams();

  if (query.select?.length && !query.select.includes("*")) {
    params.set("select", query.select.join(","));
  }
  if (query.order?.length) {
    params.set(
      "order",
      query.order.map((o) => `${o.column}.${o.ascending ? "asc" : "desc"}`).join(","),
    );
  }
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset != null && query.offset > 0) params.set("offset", String(query.offset));
  if (query.count) params.set("count", "exact");
  if (query.head) params.set("head", "true");

  // PostgREST's spelling: `or=(code.ilike.%x%,name.ilike.%x%)`. It goes in one
  // parameter because the members are alternatives to each other, where repeated
  // column parameters are conjunctions.
  if (query.or?.length) {
    params.set("or", `(${query.or.map(encodeMember).join(",")})`);
  }

  for (const filter of query.filters ?? []) {
    const prefix = filter.negate ? "not." : "";
    params.append(filter.column, `${prefix}${filter.op}.${encodeValue(filter.op, filter.value)}`);
  }

  return params.toString();
}

function decodeValue(op: FilterOperator, raw: string): unknown {
  if (op === "in") {
    const body = raw.startsWith("(") && raw.endsWith(")") ? raw.slice(1, -1) : raw;
    return body === "" ? [] : body.split(",");
  }
  if (op === "is") {
    if (raw === "null") return null;
    if (raw === "true") return true;
    if (raw === "false") return false;
    return raw;
  }
  // `eq.` with nothing after it is how an explicit null reaches us, and the
  // comparison layer already treats `eq null` as `is null`.
  return raw === "" ? null : raw;
}

export interface DecodedListQuery extends ListQuery {
  /** Parameters that matched no column filter grammar, for a clear 400. */
  rejected: string[];
}

/**
 * Parses list options back out of query parameters.
 *
 * `columns` gates which keys may become filters, so a typo is reported rather
 * than silently ignored — a filter that never applies would otherwise read as a
 * wider result set than the caller asked for.
 */
export function decodeListQuery(
  params: Record<string, string | undefined>,
  multiValue: Record<string, string[] | undefined> = {},
  columns?: readonly string[],
): DecodedListQuery {
  const out: DecodedListQuery = { rejected: [] };
  const filters: Filter[] = [];

  for (const [key, single] of Object.entries(params)) {
    if (single === undefined) continue;

    switch (key) {
      case "select":
        out.select = splitList(single);
        continue;
      case "order": {
        // Checked against the column list for the same reason a filter is: an
        // unknown sort column would otherwise compare `undefined` to
        // `undefined` on every row and quietly return them in storage order,
        // which reads as "the sort does not work" with nothing to point at.
        const order: OrderBy[] = [];
        for (const part of splitList(single)) {
          const [column, direction] = splitLast(part);
          if (columns && !columns.includes(column)) {
            out.rejected.push(`order=${column}`);
            continue;
          }
          order.push({ column, ascending: direction !== "desc" });
        }
        if (order.length) out.order = order;
        continue;
      }
      case "or": {
        const group = parseOrGroup(single, columns, out.rejected);
        if (group.length) out.or = group;
        continue;
      }
      case "limit": {
        const limit = Number(single);
        if (Number.isFinite(limit) && limit >= 0) out.limit = limit;
        continue;
      }
      case "offset": {
        const offset = Number(single);
        if (Number.isFinite(offset) && offset >= 0) out.offset = offset;
        continue;
      }
      case "count":
        out.count = single === "exact" || single === "true";
        continue;
      case "head":
        out.head = single === "true";
        continue;
    }

    if (columns && !columns.includes(key)) {
      out.rejected.push(key);
      continue;
    }

    // API Gateway hands repeated parameters over separately, so `id=in.(…)`
    // alongside `id=neq.…` both survive.
    for (const raw of multiValue[key] ?? [single]) {
      const filter = parseFilter(key, raw);
      if (filter) filters.push(filter);
      else out.rejected.push(`${key}=${raw}`);
    }
  }

  if (filters.length) out.filters = filters;
  return out;
}

/**
 * Parses `(code.ilike.%x%,name.ilike.%x%)` into the alternatives it lists.
 *
 * Members are split on the commas between them, not on the commas inside an
 * `in.(a,b)` list, so an id set can sit in an OR group alongside a text match.
 */
function parseOrGroup(
  raw: string,
  columns: readonly string[] | undefined,
  rejected: string[],
): Filter[] {
  const body = raw.startsWith("(") && raw.endsWith(")") ? raw.slice(1, -1) : raw;
  const group: Filter[] = [];

  for (const member of splitMembers(body)) {
    const filter = parseMember(member, columns);
    if (filter) group.push(filter);
    else rejected.push(`or=${member}`);
  }

  return group;
}

/**
 * Parses one OR member, `column.op.value` or `column.not.op.value`.
 *
 * Unlike a standalone column parameter, a member must spell its operator out:
 * there is no bare-value shorthand to fall back on, because a member with no
 * operator is far more likely to be a mangled query string than an intended
 * equality — and quietly reading it as one would widen the result set.
 */
function parseMember(member: string, columns?: readonly string[]): Filter | null {
  const first = member.indexOf(".");
  if (first === -1) return null;

  const column = member.slice(0, first);
  if (columns && !columns.includes(column)) return null;

  let rest = member.slice(first + 1);
  let negate = false;
  if (rest.startsWith("not.")) {
    negate = true;
    rest = rest.slice(4);
  }

  const dot = rest.indexOf(".");
  if (dot === -1) return null;

  const op = rest.slice(0, dot) as FilterOperator;
  if (!FILTER_OPERATORS.includes(op)) return null;

  return { column, op, value: decodeValue(op, decodeMember(rest.slice(dot + 1))), negate };
}

/** Undoes `encodeMember`'s inner encoding, leaving a malformed escape as typed. */
function decodeMember(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Splits on the commas that are not inside parentheses. */
function splitMembers(body: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";

  for (const character of body) {
    if (character === "(") depth += 1;
    else if (character === ")") depth -= 1;
    else if (character === "," && depth === 0) {
      if (current) parts.push(current);
      current = "";
      continue;
    }
    current += character;
  }

  if (current) parts.push(current);
  return parts;
}

function parseFilter(column: string, raw: string): Filter | null {
  let rest = raw;
  let negate = false;

  if (rest.startsWith("not.")) {
    negate = true;
    rest = rest.slice(4);
  }

  const dot = rest.indexOf(".");
  // A bare `?status=active` is the common case and means `eq`.
  const op = (dot === -1 ? "eq" : rest.slice(0, dot)) as FilterOperator;
  if (!FILTER_OPERATORS.includes(op)) {
    return dot === -1 ? { column, op: "eq", value: rest, negate } : null;
  }

  return { column, op, value: decodeValue(op, dot === -1 ? rest : rest.slice(dot + 1)), negate };
}

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Splits `created_at.desc` into its column and direction. */
function splitLast(part: string): [string, string | undefined] {
  const dot = part.lastIndexOf(".");
  if (dot === -1) return [part, undefined];
  const tail = part.slice(dot + 1);
  return tail === "asc" || tail === "desc" ? [part.slice(0, dot), tail] : [part, undefined];
}

// ---------------------------------------------------------------------------
// Payroll, leave and attendance
// ---------------------------------------------------------------------------

export const EMPLOYEES_PATH = "/employees";
/** A contract's recurring additions and deductions — a loan, an allowance. */
export const employeePayItemsPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/pay-items`;
/**
 * One employee's leave position for a year: every type, with entitlement,
 * carried-in, taken, booked and remaining already worked out.
 *
 * A computed read rather than a table: the balance is the allowance less the
 * half-days on the attendance grid, and having the browser assemble that is a
 * request per type plus a month-by-month walk of the grid — against a figure
 * that decides whether a leave request can be approved.
 */
export const employeeLeaveBalancesPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/leave-balances`;

/**
 * What this employee is paid, registered as, and paid into.
 *
 * Its own endpoint because it is its own table with its own read policy: the
 * payroll roles may read a salary and nobody else may. `GET /employees/{id}`
 * folds it into the person for a caller entitled to it and leaves it out for
 * everybody else, so one request draws the whole record at whatever depth the
 * reader is allowed — and `PATCH /employees/{id}` splits a form's payload back
 * across the two rows. This path is for the contract on its own.
 */
export const employeeContractPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/contract`;

/**
 * This person's access, administered where the person is.
 *
 * Roles used to live on their own screen over their own list of accounts, which
 * meant two pages about one human being and an email-matching sweep to work out
 * which rows on each were the same person. The roles hang off the employee now:
 * `POST` grants one, `DELETE` revokes one, both restricted to the
 * administrative roles rather than the payroll ones — setting somebody's salary
 * and granting them the run of the system are different trusts.
 */
export const employeeRolesPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/roles`;

/**
 * The sign-in account, as something the person has rather than something they
 * are.
 *
 * `POST` invites one: Cognito creates the account for the address on the
 * employee record, mails the temporary password, and the new `sub` is written
 * straight onto `user_id`. That is the whole reason this exists — an account
 * created from the person it belongs to is linked at birth, so the link is never
 * a thing somebody has to repair afterwards.
 *
 * `DELETE` removes the account and leaves the person: somebody who leaves stops
 * being able to sign in long before their payslips stop being records.
 */
export const employeeAccountPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/account`;

/**
 * `POST /employees/adopt-account` — give an existing login a person.
 *
 * The one case the invite above cannot cover: an account that already exists
 * with nobody behind it, because somebody signed themselves up or because it
 * predates all of this. `GET /employees` reports those accounts alongside the
 * people, and this turns one of them into an employee record in a single step —
 * which is the only honest way to clear that list, since what is missing is a
 * person and not a link.
 */
export const employeeAdoptAccountPath = `${EMPLOYEES_PATH}/adopt-account`;

/**
 * Documents held about an employee, and the two-step upload they use.
 *
 * `POST .../documents/upload-url` returns a presigned PUT; the browser sends the
 * file straight to S3 and then `POST .../documents` registers it. The file never
 * passes through a Lambda, so nothing is bounded by API Gateway's payload limit
 * and a ten-megabyte scan of a passport is as ordinary as a one-page letter.
 */
export const employeeDocumentsPath = (employeeId: string) =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/documents`;
export const employeeDocumentUploadUrlPath = (employeeId: string) =>
  `${employeeDocumentsPath(employeeId)}/upload-url`;
export const employeeDocumentDownloadPath = (employeeId: string, documentId: string) =>
  `${employeeDocumentsPath(employeeId)}/${encodeURIComponent(documentId)}/download`;

/**
 * The statutory rates, as one record rather than a collection.
 *
 * `GET /payroll-rates` answers with the record itself — not a page of them — and
 * `PUT /payroll-rates` saves it, creating it the first time. There is nothing to
 * pick between and nothing to delete, so neither an id in the path nor the other
 * three verbs would mean anything.
 */
export const PAYROLL_RATES_PATH = "/payroll-rates";

/** The one `payroll_rates` row's id. Fixed, so every reader asks for the same row. */
export const PAYROLL_RATES_ID = "current";
export const LEAVE_TYPES_PATH = "/leave-types";
export const LEAVE_ALLOWANCES_PATH = "/leave-allowances";
/**
 * One employee's whole year of entitlements, set in a single request.
 *
 * A `PUT` of the set rather than a `PATCH` per type, for the same reason a
 * bundle's items and a schedule's prices are replaced whole: the thing being
 * decided is "this is what they get this year", and as a write per type from the
 * browser a failure halfway through leaves somebody on next year's vacation
 * allowance and last year's sick leave, with nothing to say so.
 */
export const leaveAllowancesSetPath = `${LEAVE_ALLOWANCES_PATH}/set`;
export const HOLIDAYS_PATH = "/holidays";
export const OFFICE_NETWORKS_PATH = "/office-networks";

/**
 * Leave requests, and the decisions taken on them.
 *
 * Approving is a route of its own rather than `PATCH status=approved`, because
 * it is not a field change: it checks the balance, splits the days at the type's
 * paid limit, and writes the half-days onto the attendance grid. A browser that
 * could set the status directly could approve leave that was never counted.
 */
export const LEAVE_REQUESTS_PATH = "/leave-requests";

/**
 * Work-from-home requests, and the decisions taken on them.
 *
 * `approve` is a route rather than `PATCH status=approved` for the same reason
 * it is on leave: it is not a field change. It re-checks that the dates contain
 * working days and that none of them belong to a closed pay period, and it is
 * the thing that makes a later clock-in from home count.
 *
 * Unlike leave, approving writes nothing onto the attendance grid — permission
 * to work from home is not a day worked. See `WfhRequest` in `entities.ts`.
 */
export const WFH_REQUESTS_PATH = "/wfh-requests";
export const wfhRequestDecisionPath = (
  requestId: string,
  decision: "approve" | "reject" | "cancel",
) => `${WFH_REQUESTS_PATH}/${encodeURIComponent(requestId)}/${decision}`;
export const leaveRequestDecisionPath = (
  requestId: string,
  decision: "approve" | "reject" | "cancel",
) => `${LEAVE_REQUESTS_PATH}/${encodeURIComponent(requestId)}/${decision}`;

/**
 * The attendance grid — what replaces the monthly worksheet.
 *
 * `GET /attendance?from=&to=` returns every employee's days in the window, so
 * one request draws the whole month for the whole team, as the sheet did.
 * A day is addressed by the pair that keys it, which is also how it reads:
 *
 *   PATCH /attendance/{employeeId}/{date}
 *
 * `POST /attendance/rollup` re-runs the proposal over a window, and is what the
 * scheduled worker calls. It only ever fills in days nobody has touched.
 */
export const ATTENDANCE_PATH = "/attendance";
export const attendanceDayPath = (employeeId: string, date: string) =>
  `${ATTENDANCE_PATH}/${encodeURIComponent(employeeId)}/${encodeURIComponent(date)}`;
export const attendanceRollupPath = `${ATTENDANCE_PATH}/rollup`;
/** Half-days still unanswered in a window — what blocks a period from closing. */
export const attendanceOpenQuestionsPath = `${ATTENDANCE_PATH}/open-questions`;

/**
 * Whose attendance the caller may open — names only, searched server-side.
 *
 * `GET /attendance/people?q=&limit=[&employee_id=]` answers the "which person"
 * question the calendar and the timesheet both start from, narrowed the same
 * way every other attendance read is: an employee finds themselves, a manager
 * finds their reports, whoever runs the payroll finds everybody.
 *
 * It exists rather than the screens reading `employees` because that row
 * carries the contract — a team member may see who is off without being able to
 * see what they earn, so the name is projected here instead. And it takes `q`
 * rather than handing the browser the register, because the list grows past
 * what a dropdown can usefully hold.
 */
export const attendancePeoplePath = `${ATTENDANCE_PATH}/people`;

/**
 * The longest window one attendance read may cover — a year of one person, or a
 * month of forty.
 *
 * Shared rather than duplicated because both ends enforce it: the handler
 * refuses a wider range, and a screen offering a date range has to be able to
 * say so before sending a request it knows will be refused.
 */
export const ATTENDANCE_MAX_RANGE_DAYS = 400;

/**
 * The timesheet — the same window as the grid, in hours rather than half-days.
 *
 * `GET /attendance/timesheet?from=&to=[&employee_id=]` returns a row per date
 * with every clock-in and clock-out on it, what the day added up to, and what
 * the month did. The grid answers "was this person working"; this answers "for
 * how long", which is a different question and the one an employee asks about
 * their own week.
 *
 * Separate from the grid rather than more fields on it, because the grid is one
 * request for forty people and a month of entries each would be a far larger
 * payload than the screen it draws needs.
 */
export const attendanceTimesheetPath = `${ATTENDANCE_PATH}/timesheet`;

/**
 * Settling one clocked entry a manager has to decide about.
 *
 * A clock-in outside somebody's working pattern is recorded and counts for
 * nothing until somebody says what it was — which has to be sayable, or the
 * strictness is just lost hours. `resolve` accepts the entry as worked time and
 * names what it was; `reject` records that it was not worked at all.
 *
 * The entry is identified in the body rather than the path, because its key is
 * a timestamp and a URL-encoded one reads badly in a route and worse in a log.
 */
export const attendanceEntryDecisionPath = (decision: "resolve" | "reject") =>
  `${ATTENDANCE_PATH}/entries/${decision}`;

/**
 * Correcting a clocked time — the back-dated fill-in, and who may make it.
 *
 *   GET  /time-amendments[?employee_id=&status=]   corrections, scoped to the caller
 *   GET  /time-amendments/allowance[?employee_id=] how many are left this month
 *   POST /time-amendments                          correct a time, or ask to
 *   POST /time-amendments/{id}/approve | reject | cancel
 *
 * One POST for both halves of the rule rather than two endpoints, because the
 * employee is making the same request either way — "this time was wrong, here
 * is the right one" — and which of the two happens is the server's call, from
 * an allowance only it can count. A browser that had to choose the endpoint
 * would be deciding its own entitlement, and would get it wrong the moment
 * somebody corrected a time in another tab.
 *
 * The response says which happened: an `applied` correction is already on the
 * entry, a `pending` one is waiting for an admin.
 */
export const TIME_AMENDMENTS_PATH = "/time-amendments";
/** How many self-service corrections are left this month, and out of how many. */
export const timeAmendmentAllowancePath = `${TIME_AMENDMENTS_PATH}/allowance`;
export const timeAmendmentDecisionPath = (
  amendmentId: string,
  decision: "approve" | "reject" | "cancel",
) => `${TIME_AMENDMENTS_PATH}/${encodeURIComponent(amendmentId)}/${decision}`;

/**
 * Pay periods, and the run that fills one.
 *
 * `calculate` is a POST rather than a PATCH for the same reason approving leave
 * is: it reads the grid, the contracts, the rates and every earlier payslip of
 * the year, and writes a payslip per employee. `approve` locks the period and
 * every attendance day it consumed, after which neither can change — a
 * correction found later lands on the next open period.
 */
export const PAY_PERIODS_PATH = "/pay-periods";
export const payPeriodActionPath = (periodId: string, action: "calculate" | "approve" | "reopen") =>
  `${PAY_PERIODS_PATH}/${encodeURIComponent(periodId)}/${action}`;
export const payPeriodPayslipsPath = (periodId: string) =>
  `${PAY_PERIODS_PATH}/${encodeURIComponent(periodId)}/payslips`;

export const PAYSLIPS_PATH = "/payslips";
export const PAYROLL_FILINGS_PATH = "/payroll-filings";

/**
 * The files behind one return. Same three-step shape as the employee documents:
 * ask for a grant, PUT the bytes straight to storage, then register the row.
 */
export const payrollFilingDocumentsPath = (filingId: string) =>
  `${PAYROLL_FILINGS_PATH}/${encodeURIComponent(filingId)}/documents`;
export const payrollFilingDocumentUploadUrlPath = (filingId: string) =>
  `${payrollFilingDocumentsPath(filingId)}/upload-url`;
export const payrollFilingDocumentDownloadPath = (filingId: string, documentId: string) =>
  `${payrollFilingDocumentsPath(filingId)}/${encodeURIComponent(documentId)}/download`;

/**
 * The top-level paths the API serves, one API Gateway mount each.
 *
 * The stack declares `ANY /<mount>` and `ANY /<mount>/{proxy+}` for every entry
 * and nothing else, so a route is reachable only if its first segment is here.
 * Declaring every route in the stack instead cost two CloudFormation resources
 * apiece — 365 of them, past the 500 a stack allows — but it did mean the stack
 * and the handlers were checked against each other on every deploy.
 *
 * This list is what replaces that check: `infra/lib/api.ts` asserts it mounts
 * exactly these, and `services/api/test/mounts.test.ts` asserts every route any
 * handler declares falls under one of them. A new top-level path has to be added
 * here before it can be served.
 */
export const API_MOUNTS = [
  "attendance",
  "bank-accounts",
  "bulk-actions",
  "client-groups",
  "companies",
  "contacts",
  "countries",
  "data",
  "employees",
  "fee-schedules",
  "holidays",
  "import",
  "invoice-notes",
  "invoice-settings",
  "invoices",
  "leave-allowances",
  "leave-requests",
  "leave-types",
  "me",
  "office-networks",
  "pay-periods",
  "payroll-filings",
  "payroll-rates",
  "payslips",
  "remittance-defaults",
  "service-bundles",
  "service-categories",
  "services",
  "time-amendments",
  "virrgin-transactions",
  "wfh-requests",
  "work-log",
] as const;

export type ApiMount = (typeof API_MOUNTS)[number];
